/* eslint-disable react/jsx-no-bind */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { memo, Fragment } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  countOverlaps,
  ItemUser,
  renderOverlapCreatedType
} from '@utils/logs-utils';
import {
  EntityComment,
  OverlapComment,
  Resolve,
  Revoke,
  WorkflowInfo,
  TaskActivityStatus,
  EntityInfo,
  getTimeSinceCreated
} from '@utils/notification-utils';
import { buildAssignedTaskTo } from '@utils/workflow-utils';
import './notification-contents.scss';

const NotificationContentsWrap = ({ contents, extra, user }) => (
  <Fragment>
    <div styleName="item-message-header">
      <div styleName="log-item-message-container">
        <ItemUser user={user} />
        {contents}
      </div>
    </div>
    {extra &&
      <div styleName="item-message-extra">
        {extra}
      </div>
    }
  </Fragment>
);

NotificationContentsWrap.propTypes = {
  contents: PropTypes.node,
  extra: PropTypes.node,
  user: PropTypes.object
};

// eslint-disable-next-line react/display-name
const NotificationContents = memo(({ action, attrs, user, created }) => {
  const users = useSelector(state => state.dataTypes.user);
  const taskStatuses = useSelector(state => state.dataTypes.task_status);
  const currentUser = useSelector(state => state.config.user);

  const timeSinceCreated = getTimeSinceCreated(created);

  switch (action) {
  case 'overlap_created': {
    const count = countOverlaps(attrs);
    if (count > 0) {
      if (count === 1) {
        return (
          <NotificationContentsWrap
            user={user}
            contents={(
              <span>
                The following {attrs.entity1.entity_type} is now in conflict with 1 {attrs.entity}. {renderOverlapCreatedType(attrs)}
                <div styleName="created">{timeSinceCreated}</div>
                <EntityInfo entity={attrs.entity1} />
              </span>
            )}
          />
        );
      }
      return (
        <NotificationContentsWrap
          user={user}
          contents={(
            <span>
              The following {attrs.entity1.entity_type} is now in conflict with {count} {attrs.entity}s. {renderOverlapCreatedType(attrs)}
              <div styleName="created">{timeSinceCreated}</div>
              <EntityInfo entity={attrs.entity1} />
            </span>
          )}
        />
      );
    } else
      if (attrs.ids) {
        if (attrs.ids.length === 1) {
          return (
            <NotificationContentsWrap
              user={user}
              contents={(
                <span>
                  The following {attrs.entity1.entity_type} is now in conflict with 1 {attrs.entity}. {renderOverlapCreatedType(attrs)}
                  <div styleName="created">{timeSinceCreated}</div>
                  <EntityInfo entity={attrs.entity1} />
                </span>
              )}
            />
          );
        }
        return (
          <NotificationContentsWrap
            user={user}
            contents={(
              <span>
                The following {attrs.entity1.entity_type} is now in conflict with {attrs.ids.length} {attrs.entity}s.
                {renderOverlapCreatedType(attrs)}
                <div styleName="created">{timeSinceCreated}</div>
                <EntityInfo entity={attrs.entity1} />
              </span>
            )}
          />
        );
      }
    return (
      <NotificationContentsWrap
        user={user}
        contents={(
          <span>
            The following {attrs.entity1.entity_type} is now in conflict with 1 {attrs.entity2.entity_type}. {renderOverlapCreatedType(attrs)}
            <div styleName="created">{timeSinceCreated}</div>
            <EntityInfo entity={attrs.entity1} />
          </span>
        )}
      />
    );
  }
  case 'opportunity_to_conflict': {
    if (attrs.ids) {
      if (attrs.ids.length === 1) {
        return (
          <NotificationContentsWrap
            user={user}
            contents={
              <span>
                The following {attrs.entity1.entity_type} is now in conflict with 1 {attrs.entity}. {renderOverlapCreatedType(attrs)}
                <div styleName="created">{timeSinceCreated}</div>
                <EntityInfo entity={attrs.entity1} />
              </span>
            }
          />
        );
      }
      return (
        <NotificationContentsWrap
          user={user}
          contents={
            <span>
              The following {attrs.entity1.entity_type} is now in conflict with {attrs.ids.length} {attrs.entity}s.
              {renderOverlapCreatedType(attrs)}
              <div styleName="created">{timeSinceCreated}</div>
              <EntityInfo entity={attrs.entity1} />
            </span>
          }
        />
      );
    }
    return (
      <NotificationContentsWrap
        user={user}
        contents={
          <span>
            The following {attrs.entity1.entity_type} is now in conflict with 1 {attrs.entity2.entity_type}. {renderOverlapCreatedType(attrs)}
            <div styleName="created">{timeSinceCreated}</div>
            <EntityInfo entity={attrs.entity1} />
          </span>
        }
      />
    );
  }
  case 'entity_comment': {
    let contentString = ` commented on a ${attrs.entity_type}`;
    const currentUserEmail = currentUser.email;
    if (attrs.mentions && attrs.mentions.includes(currentUserEmail)) {
      contentString = ' mentioned you in a comment';
    }
    return (
      <NotificationContentsWrap
        user={user}
        contents={<span>{contentString}</span>}
        extra={<EntityComment attrs={attrs} users={users} />}
      />
    );
  }
  case 'task_activity_comment': {
    return (
      <NotificationContentsWrap
        user={user}
        contents={<span> commented on a task</span>}
        extra={<div>
          <WorkflowInfo attrs={attrs} />
          <OverlapComment attrs={attrs} users={users} />
        </div>}
      />
    );
  }
  case 'task_assign': {
    const to = buildAssignedTaskTo(attrs);
    return (
      <NotificationContentsWrap
        user={user}
        contents={<span> has assigned a task to {to}</span>}
        extra={<WorkflowInfo attrs={attrs} />}
      />
    );
  }
  case 'task_activity_status_update': {
    const statuses = Object.values(taskStatuses);
    const oldStatus = statuses.find(status => status.name === attrs.old_status);
    const newStatus = statuses.find(status => status.name === attrs.new_status);
    return (
      <NotificationContentsWrap
        user={user}
        contents={<span> transitioned a task</span>}
        extra={<div>
          <WorkflowInfo attrs={attrs} />
          <TaskActivityStatus
            attrs={attrs}
            oldStatus={oldStatus}
            newStatus={newStatus}
          />
        </div>}
      />
    );
  }
  case 'overlap_comment': {
    return (
      <NotificationContentsWrap
        user={user}
        contents={<span> commented on a conflict.</span>}
        extra={<OverlapComment attrs={attrs} users={users} />}
      />
    );
  }
  case 'overlap_resolve': {
    return (
      <NotificationContentsWrap
        user={user}
        contents={<span> resolved a conflict.</span>}
        extra={<Resolve attrs={attrs} users={users} />}
      />
    );
  }
  case 'overlap_revoke': {
    return (
      <NotificationContentsWrap
        user={user}
        contents={<span> revoked a resolution.</span>}
        extra={<Revoke attrs={attrs} users={users} />}
      />
    );
  }
  default:
    return null;
  }
});

NotificationContents.propTypes = {
  action: PropTypes.string,
  attrs: PropTypes.object,
  created: PropTypes.string,
  user: PropTypes.object
};

export default NotificationContents;
